import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { NotificationToast } from '@ds';
import { useNavigate } from 'react-router-dom';
import { NotificationToastType } from '@/api/apis/communication';
import { toastNotifier } from '@/ws';
import { getBodyForToastNotification, getTypeForToastNotification } from '@/utils/helpers/notifications.helpers';
import { SettingsType } from '@/api/apis/user';
import { getCaseToastLink } from './utils';

export const useNotifications = (
  settingsData: SettingsType | undefined,
  isNopConnectPlatform: boolean = false,
  customerInTxCenterCodes: string[] = [],
) => {
  const [notificationCount, setNotificationCount] = useState(0);
  const navigate = useNavigate();

  const closeAllToast = () => {
    toast.dismiss();
    setNotificationCount(0);
  };

  const customTextForNotificationBody = useCallback(
    (customText: string | null | undefined, body: string): string => {
      if (!isNopConnectPlatform && customText) {
        return customText;
      }
      return body || '';
    },
    [isNopConnectPlatform],
  );

  useEffect(() => {
    if (settingsData?.toast) {
      return toastNotifier.subscribe((notification: NotificationToastType) => {
        const txcCode = notification?.data?.txcCode;

        if (!isNopConnectPlatform && txcCode && customerInTxCenterCodes.length) {
          const isCodeAvailable = customerInTxCenterCodes.some((code: string) => code === txcCode);

          if (!isCodeAvailable) return;
        }
        const notificationBody = getBodyForToastNotification({
          body: customTextForNotificationBody(notification?.data?.CustomerText, notification.body),
          notification,
          isNopConnectPlatform,
        });

        const notificationVariant = getTypeForToastNotification({
          notificationType: notification?.toastType,
          data: notification?.data,
          body: notification?.body,
        });

        setNotificationCount((prevCount) => prevCount + 1);

        toast(
          <NotificationToast
            onNavigate={navigate}
            title={notification?.title}
            variant={notificationVariant}
            txcCode={notification.data?.txcCode}
            link={getCaseToastLink(notification, isNopConnectPlatform)}
          >
            {notificationBody}
          </NotificationToast>,
          {
            autoClose: 10000,
            pauseOnFocusLoss: true,
            position: 'top-right',
            className: 'ws-notifications',
            closeButton: true,
            pauseOnHover: true,
            onClose: () => {
              setNotificationCount((prevCount) => prevCount - 1);
            },
          },
        );
      });
    }

    return () => {};
  }, [customTextForNotificationBody, customerInTxCenterCodes, isNopConnectPlatform, navigate, settingsData]);

  return { notificationCount, closeAllToast };
};
